import React, { useMemo, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  styled,
} from "@mui/material";
import { borderSize, colors, fontSize, fontWeight, margin, padding } from "theme/defaultStyle";
import ViewEyeIcon from "theme/icons/ViewEyeIcon";
import ViewMessageDialog from "./ViewMessageDialog";

// ----- Types -----
type UWAStatus = "Approved" | "Approved with Conditions" | string;

const TYPE_ORDER = ["Conditions", "Endorsements", "Notes"] as const;
type UWAActivityTypeCode = (typeof TYPE_ORDER)[number];

interface ApiActivity {
  Type: string;
  Description: string;
  UWAActivityId: number | string;
  DateEntered: string;
}
type ActivitiesByType = Partial<Record<UWAActivityTypeCode, ApiActivity[]>>;

type AggregatedStrings = Partial<Record<UWAActivityTypeCode, string | null>>;
type AggregatedStringsArray = AggregatedStrings[];

type ActivitiesInput =
  | ActivitiesByType
  | AggregatedStrings
  | AggregatedStringsArray
  | { apiResponse?: ActivitiesByType | AggregatedStrings | AggregatedStringsArray }
  | null
  | undefined;

interface ApprovalItemsSectionProps {
  createdFromMainPortal: boolean;
  status: UWAStatus;
  activities: ActivitiesInput;
}

// ----- Styled -----
const Container = styled(Box)({
  width: "100%",
  padding: padding.xlarge1,
  border: `${borderSize.xsmall} solid ${colors.grey04}`,
  boxSizing: "border-box",
});

const SectionTitle = styled(Typography)({
  color: colors.blue01,
  fontSize: fontSize.xlarge,
  fontStyle: "normal",
  fontWeight: fontWeight.bold1,
  lineHeight: "normal",
  marginBottom: margin.large,
});

const StyledTable = styled(Table)({
  width: "100%",
  borderCollapse: "collapse",
  borderRadius: "10px",
});

const HeaderCell = styled(TableCell)({
  color: "var(--Agent-Text-Main, #212121)",
  fontFamily: "Inter",
  fontSize: fontSize.medium,
  fontStyle: "normal",
  fontWeight: fontWeight.bold1,
  textTransform: "uppercase",
  borderBottom: `1px solid var(--Agent-Input-Default-Stroke, ${colors.grey09})`,
});

const TypeHeaderCell = styled(HeaderCell)({
  width: 180,
});

const Body = styled(TableBody)({
  "--stroke": `var(--Agent-Input-Default-Stroke, ${colors.grey09})`,
  "--bg": colors.grey02 ?? "#F5F5F5",
  "& tr:first-of-type td": {
    borderTop: `1px solid var(--Agent-Input-Default-Stroke, ${colors.grey09})`,
  },
  "& tr td:first-of-type": {
    borderLeft: `1px solid var(--Agent-Input-Default-Stroke, ${colors.grey09})`,
  },
  "& tr td:last-of-type": {
    borderRight: `1px solid var(--Agent-Input-Default-Stroke, ${colors.grey09})`,
  },
  "& tr:last-of-type td": {
    borderBottom: `1px solid var(--Agent-Input-Default-Stroke, ${colors.grey09})`,
  },
  "& tr:first-of-type td:first-of-type": { borderTopLeftRadius: 6 },
  "& tr:first-of-type td:last-of-type": { borderTopRightRadius: 6 },
  "& tr:last-of-type td:first-of-type": { borderBottomLeftRadius: 6 },
  "& tr:last-of-type td:last-of-type": { borderBottomRightRadius: 6 },
  "& td": { background: "var(--bg)" },
  "& tr + tr td": {
    borderTop: `1px solid var(--Agent-Input-Default-Stroke, ${colors.grey09})`,
  },
});

const TypeCell = styled(TableCell)({
  verticalAlign: "top",
  color: `var(--Agent-Text-Disabled, ${colors.grey27})`,
  fontFamily: "Inter",
  fontSize: fontSize.medium,
  fontStyle: "normal",
  fontWeight: fontWeight.normal1,
  lineHeight: "20px",
  padding: `${padding.small2} ${padding.medium1}`,
});

const DescCell = styled(TableCell)({
  padding: 0,
  verticalAlign: "top",
});

const DescInner = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr auto",
  columnGap: 8,
  alignItems: "start", 
  padding: `${padding.small2} ${padding.medium1}`,
  minHeight: 44,
});

const WrappedText = styled("div")({
  flex: 1,
  lineHeight: "1.6em",
  whiteSpace: "pre-wrap", // "pre-line",     
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  color: `var(--Agent-Text-Disabled, ${colors.grey27})`,
  fontFamily: "Inter",
  fontSize: fontSize.small1,
  maxHeight: 'calc(1.6em * 10)', // 10 lines
  overflow: 'hidden',
});

const ViewIconBtn = styled(IconButton)({
  alignSelf: "start",
  marginTop: 2,
  color: colors.blue01,
});

// ----- Utils -----
const APPROVED_STATUSES: readonly UWAStatus[] = ["Approved", "Approved with Conditions"];
function isApproved(status: UWAStatus) {
  return (APPROVED_STATUSES as readonly string[]).includes(status);
}

function htmlToFlatSentence(html: string): string {
  if (!html) return "";
  return html
    .replace(/<\/div>\s*<div>/g, "; ") 
    .replace(/<\/?div>/g, "")          
    .replace(/<[^>]+>/g, "")          
    .replace(/&nbsp;/g, " ")
    .replace(/\s*;\s*/g, "; ")         
    .replace(/\s+/g, " ")             
    .trim();
}

function normalizeFromBucketArrays(obj: ActivitiesByType): Record<UWAActivityTypeCode, string> {
  const out: Record<UWAActivityTypeCode, string> = { Conditions: "", Endorsements: "", Notes: "" };
  TYPE_ORDER.forEach((key) => {
    const arr = obj[key] ?? [];
    const joined = arr
      .map((a) => htmlToFlatSentence(a?.Description ?? ""))
      .filter(Boolean)
      .join("; ");
    if (joined) out[key] = joined;
  });
  return out;
}

function normalizeFromAggregatedStrings(obj: AggregatedStrings): Record<UWAActivityTypeCode, string> {
  const out: Record<UWAActivityTypeCode, string> = { Conditions: "", Endorsements: "", Notes: "" };
  TYPE_ORDER.forEach((key) => {
    const joined = htmlToFlatSentence(obj[key] ?? "");
    if (joined) out[key] = joined;
  });
  return out;
}

function normalizeActivities(input: ActivitiesInput): Record<UWAActivityTypeCode, string> {
  const empty: Record<UWAActivityTypeCode, string> = { Conditions: "", Endorsements: "", Notes: "" };
  if (!input) return empty;

  const unwrapped =
    (typeof input === "object" && input && "apiResponse" in input
      ? (input as any).apiResponse
      : input) as ActivitiesByType | AggregatedStrings | AggregatedStringsArray | null;

  if (!unwrapped) return empty;

  const root = Array.isArray(unwrapped) ? (unwrapped[0] ?? null) : unwrapped;
  if (!root || typeof root !== "object") return empty;

  const looksLikeBucketArrays =
    TYPE_ORDER.some((k) => Array.isArray((root as any)[k])) &&
    (TYPE_ORDER as readonly string[]).every(
      (k) => (root as any)[k] === undefined || Array.isArray((root as any)[k])
    );

  if (looksLikeBucketArrays) {
    return normalizeFromBucketArrays(root as ActivitiesByType);
  }

  return normalizeFromAggregatedStrings(root as AggregatedStrings);
}

const ApprovalItemsSection: React.FC<ApprovalItemsSectionProps> = ({
  createdFromMainPortal,
  status,
  activities,
}) => {
  const perType = useMemo(() => normalizeActivities(activities), [activities]);

  const rows = useMemo(
    () =>
      TYPE_ORDER
        .map((t) => ({ type: t, text: perType[t] }))
        .filter((r) => !!r.text && r.text.trim().length > 0),
    [perType]
  );

  const showSection = useMemo(() => {
    if (!createdFromMainPortal) return false;
    if (!isApproved(status)) return false;
    return rows.length > 0;
  }, [createdFromMainPortal, status, rows]);

  const [openType, setOpenType] = useState<UWAActivityTypeCode | null>(null);

  const openText = openType ? perType[openType] : "";

  const dialogHeader = useMemo(() => (openType ? `View Full ${openType}` : ""), [openType]);

  if (!showSection) return null;

  return (
    <Container>
      <SectionTitle>Approval Items</SectionTitle>

      <StyledTable size="small" aria-label="approval items">
        <TableHead>
          <TableRow>
            <TypeHeaderCell>Type</TypeHeaderCell>
            <HeaderCell>Description</HeaderCell>
          </TableRow>
        </TableHead>

        <Body>
          {rows.map(({ type, text }) => (
            <TableRow key={type}>
              <TypeCell>{type}</TypeCell>
              <DescCell>
                <DescInner>
                  <WrappedText>{text}</WrappedText>
                  <ViewIconBtn
                    size="small"
                    aria-label="view full"
                    onClick={() => setOpenType(type)}
                    color="secondary"
                  >
                    <ViewEyeIcon />
                  </ViewIconBtn>
                </DescInner>
              </DescCell>
            </TableRow>
          ))}
        </Body>
      </StyledTable>

      <ViewMessageDialog
        isOpen={!!openType}
        onClose={() => setOpenType(null)}
        message={null}
        messageText={openText}
        headerText={dialogHeader}
      />
    </Container>
  );
};

export default ApprovalItemsSection;
